import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Heart, Star } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";

// Split biography by ## headings — each becomes a chapter block
const parseBio = (raw) => {
  if (!raw) return [];
  const parts = raw.split(/\n(?=## )/);
  return parts.map((p) => {
    const m = p.match(/^## (.+)/);
    if (m) {
      return { title: m[1].trim(), body: p.slice(m[0].length).trim() };
    }
    return { title: "", body: p.trim() };
  }).filter((c) => c.body || c.title);
};

// Render bold **text** and preserve line breaks
const renderChapter = (text) =>
  text.split(/\n\n+/).map((para, i) => (
    <p key={i} className="text-imi-text/75 text-lg md:text-xl leading-[1.75] mb-6">
      {para.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
        chunk.startsWith("**") && chunk.endsWith("**") ? (
          <strong key={j} className="text-imi-goldlight font-semibold">{chunk.slice(2, -2)}</strong>
        ) : (
          <span key={j}>{chunk}</span>
        )
      )}
    </p>
  ));

export default function HaroldPage() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    document.title = "Harold Cunningham Jr. · Biography · IMI";
    window.scrollTo(0, 0);
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const bio = settings?.founder_biography_full || "";
  const chapters = parseBio(bio);
  const intro = chapters.find((c) => !c.title)?.body || "";
  const sections = chapters.filter((c) => c.title);
  const img = settings?.founder_image ? mediaUrl({ media_id: settings.founder_image }) : "/founder-harold.png";

  return (
    <div className="min-h-screen flex flex-col bg-imi-bg text-imi-text">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-imi-border overflow-hidden">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-imi-text/50 hover:text-imi-gold text-[11px] uppercase tracking-[0.25em] mb-10">
              <ArrowLeft size={14} /> Back to IMI
            </Link>
            <div className="grid md:grid-cols-12 gap-12 items-center">
              <Reveal className="md:col-span-5">
                <div className="aspect-[4/5] overflow-hidden border border-imi-gold/30 relative">
                  <img src={img} alt="Harold Cunningham Jr." className="w-full h-full object-cover" style={{ objectPosition: "center 15%" }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-imi-bg/60 via-transparent to-transparent" />
                </div>
              </Reveal>
              <Reveal className="md:col-span-7">
                <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-6">Biography</p>
                <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                  Harold <span className="italic gold-gradient-text">Cunningham Jr.</span>
                </h1>
                <p className="mt-4 text-imi-goldlight text-lg md:text-xl">Writer · Advocate · Prison Reform Leader</p>
                <div className="mt-10 flex flex-wrap gap-6 text-imi-text/60">
                  <Stat label="Years Incarcerated" value="35+" />
                  <Stat label="Years in Solitary" value="20+" />
                  <Stat label="Inmates Freed by Lawsuit" value="100+" />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Intro paragraph */}
        {intro && (
          <section className="py-20 md:py-28 border-b border-imi-border">
            <div className="max-w-[900px] mx-auto px-6 md:px-10">
              <Reveal>
                <div className="text-imi-text/85 font-serif text-2xl md:text-3xl leading-[1.5]" data-testid="biography-intro">
                  {intro.split(/\n\n+/).map((p, i) => <p key={i} className="mb-6">{p}</p>)}
                </div>
              </Reveal>
            </div>
          </section>
        )}

        {/* Chaptered body */}
        <section className="py-16 md:py-24">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-16 md:space-y-20">
            {sections.map((c, i) => (
              <Reveal key={i}>
                <div className="grid md:grid-cols-12 gap-6" data-testid={`biography-chapter-${i}`}>
                  <div className="md:col-span-3">
                    <div className="sticky top-32">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-imi-gold mb-2">Ch. {String(i + 1).padStart(2, "0")}</div>
                      <div className="h-px w-10 bg-imi-gold mb-4" />
                      <h2 className="font-serif text-imi-goldlight text-xl md:text-2xl leading-tight">{c.title}</h2>
                    </div>
                  </div>
                  <div className="md:col-span-9">{renderChapter(c.body)}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CTA footer */}
        <section className="border-t border-imi-border py-20 md:py-28 bg-imi-surface">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
            <Reveal>
              <Star size={32} className="text-imi-gold mx-auto mb-6" />
              <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">
                Stand with Harold. <span className="italic gold-gradient-text">Fund the mission.</span>
              </h2>
              <p className="mt-6 text-imi-text/60 text-lg leading-relaxed max-w-xl mx-auto">
                Your support keeps the films made, the podcasts recorded, and the fight for those still inside alive.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 justify-center">
                <Link to="/#support" data-testid="harold-cta-donate" className="inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-6 py-4 text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors">
                  Support IMI <Heart size={14} />
                </Link>
                <Link to="/tell-their-story" data-testid="harold-cta-story" className="inline-flex items-center gap-2 border border-imi-gold/40 text-imi-gold px-6 py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-imi-gold/10 transition-colors">
                  Share a story <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer settings={settings} />
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="border-l-2 border-imi-gold/40 pl-4">
    <div className="font-serif text-imi-goldlight text-3xl md:text-4xl leading-none">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-imi-text/50 mt-2">{label}</div>
  </div>
);
